/**
 * Gestionnaire de zones de prospection
 * Permet de créer, éditer et supprimer des zones sur la carte
 */

import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import type { ProspectionZone, DrawMode, Coordinate } from '../../types/prospectionZone';
import {
  getProspectionZones,
  deleteProspectionZone,
  toggleProspectionZone
} from '../../services/supabase/prospectionZones.service';
import { useAuth } from '../../context/AuthContext';
import { eventsService } from '../../services/events.service';
import ZoneList from './ZoneList';
import ZoneForm from './ZoneForm';
import './ZoneManager.css';

interface ZoneManagerProps {
  onDrawModeChange: (mode: DrawMode) => void;
  onZoneSelect: (zoneId: string | null) => void;
  onPointAdded?: (coordinate: Coordinate) => void;
  onDrawingComplete?: (coordinates: Coordinate[]) => void;
  onZonesLoaded?: (zones: ProspectionZone[]) => void;
  selectedZoneId: string | null;
  drawMode: DrawMode;
  drawingCoordinates?: Coordinate[];
}

const ZoneManager: React.FC<ZoneManagerProps> = ({
  onDrawModeChange,
  onZoneSelect,
  onPointAdded: onPointAddedProp,
  onDrawingComplete: onDrawingCompleteProp,
  onZonesLoaded,
  selectedZoneId,
  drawMode,
  drawingCoordinates = []
}) => {
  const { userProfile } = useAuth();
  const [zones, setZones] = useState<ProspectionZone[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showList, setShowList] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Charger les zones au montage
  useEffect(() => {
    loadZones();
  }, [userProfile?.agency_id]);

  // Écouter l'événement RELOAD_ZONES pour recharger après création
  useEffect(() => {
    const handleReloadZones = () => {
      console.log('🔄 ZoneManager: Rechargement des zones demandé');
      loadZones();
    };

    const unsubscribe = eventsService.subscribe('RELOAD_ZONES', handleReloadZones, 'zone-manager-reload');
    
    return () => {
      unsubscribe();
    };
  }, [userProfile?.agency_id]);

  const loadZones = async () => {
    if (!userProfile?.agency_id) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await getProspectionZones({
        agencyId: userProfile.agency_id,
        activeOnly: false
      });
      setZones(data);
      onZonesLoaded?.(data);
    } catch (err) {
      console.error('Error loading zones:', err);
      setError('Erreur lors du chargement des zones');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartDrawing = () => {
    setShowForm(true);
    setShowList(false);
    onDrawModeChange('drawing');
  };

  const handleCancelDrawing = () => {
    setShowForm(false);
    onDrawModeChange('none');
    // Notifier le parent pour vider les points
    onDrawingCompleteProp?.([]);
  };

  const handleZoneCreated = () => {
    setShowForm(false);
    onDrawModeChange('none');
    loadZones();
    // Notifier le parent pour vider les points
    onDrawingCompleteProp?.([]);
  };

  const handleToggleList = () => {
    setShowList(!showList);
    if (showForm) {
      setShowForm(false);
      onDrawModeChange('none');
    }
  };

  const handleZoneToggle = async (zoneId: string, isActive: boolean) => {
    try {
      await toggleProspectionZone(zoneId, isActive);
      loadZones();
    } catch (err) {
      console.error('Error toggling zone:', err);
      setError('Erreur lors de la modification de la zone');
    }
  };

  const handleZoneDelete = async (zoneId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette zone ?')) {
      return;
    }

    try {
      await deleteProspectionZone(zoneId);
      if (selectedZoneId === zoneId) {
        onZoneSelect(null);
      }
      loadZones();
    } catch (err) {
      console.error('Error deleting zone:', err);
      setError('Erreur lors de la suppression de la zone');
    }
  };

  const handleZoneSelect = (zoneId: string | null) => {
    onZoneSelect(zoneId);
  };

  return (
    <div className="zone-manager">
      {/* Boutons de contrôle - DÉSACTIVÉS car gérés par la sidebar */}
      {/* La fonctionnalité de dessin est accessible via le bouton + dans ZonesSection */}
      {/* La liste des zones est accessible via l'onglet Zones dans la sidebar */}
      {/* 
      <div className="zone-manager-controls">
        <button
          className={`zone-control-btn ${drawMode === 'drawing' ? 'active' : ''}`}
          onClick={handleStartDrawing}
          disabled={drawMode === 'drawing'}
          title="Dessiner une zone"
        >
          <FaMapMarkerAlt />
          <span>Dessiner</span>
        </button>

        <button
          className={`zone-control-btn ${showList ? 'active' : ''}`}
          onClick={handleToggleList}
          title="Liste des zones"
        >
          <FaPlus />
          <span>Zones ({zones.filter(z => z.isActive).length})</span>
        </button>
      </div>
      */}

      {/* Message d'erreur */}
      {error && (
        <div className="zone-manager-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <FaTimes />
          </button>
        </div>
      )}

      {/* Formulaire de création */}
      {showForm && (
        <ZoneForm
          coordinates={drawingCoordinates}
          onCancel={handleCancelDrawing}
          onSuccess={handleZoneCreated}
        />
      )}

      {/* Liste des zones */}
      {showList && (
        <ZoneList
          zones={zones}
          selectedZoneId={selectedZoneId}
          isLoading={isLoading}
          onZoneSelect={handleZoneSelect}
          onZoneToggle={handleZoneToggle}
          onZoneDelete={handleZoneDelete}
          onClose={() => setShowList(false)}
        />
      )}
    </div>
  );
};

export default ZoneManager;
